import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Alert,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const AdvanceEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/advances",
    token
  );

  // Fetch foreign key data: employees and currencies.
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employee"],
    "shop/employees",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const employees = useMemo(
    () => (Array.isArray(employeesData) ? employeesData : employeesData?.results || []),
    [employeesData]
  );
  const currencies = useMemo(
    () =>
      getUniqueCurrencies(
        Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
      ),
    [currenciesData]
  );

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");
  const initialRateSelectionRef = useRef("");
  const selectedEmployeeDetails = useMemo(
    () => employees.find((emp) => String(emp.id) === String(selectedEmployee)) || null,
    [employees, selectedEmployee]
  );
  const selectedCurrencyDetails = useMemo(
    () => currencies.find((cur) => String(cur.id) === String(selectedCurrency)) || null,
    [currencies, selectedCurrency]
  );
  const selectedShopId =
    selectedEmployeeDetails?.shop?.id ||
    selectedEmployeeDetails?.shop ||
    selectedCurrencyDetails?.shop?.id ||
    selectedCurrencyDetails?.shop ||
    editData?.shop_details?.id ||
    editData?.shop?.id ||
    editData?.shop ||
    "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "advance-edit", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = useMemo(
    () => (Array.isArray(accountsData) ? accountsData : accountsData?.results || []),
    [accountsData]
  );
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(selectedCurrency)),
    [accounts, selectedCurrency]
  );

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedEmployee(
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : ""
      );
      setSelectedCurrency(
        editData.currency_details && editData.currency_details.id
          ? String(editData.currency_details.id)
          : editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : ""
      );
      const accountId = editData.account_details?.id
        ? String(editData.account_details.id)
        : editData.account
          ? String(editData.account)
          : "";
      setSelectedAccountId(accountId);
      setExchangeRate(editData.exchange_rate || "");
      setAmount(editData.amount || "");
      setYear(editData.year || "");
      setMonth(editData.month || "");
      setPaymentDate(
        editData.payment_date ? editData.payment_date.substring(0, 16) : ""
      );
      setNotes(editData.notes || "");
      const currencyId = editData.currency_details?.id
        ? String(editData.currency_details.id)
        : editData.currency
          ? String(editData.currency)
          : "";
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData]);

  useEffect(() => {
    if (editData && employees && employees.length > 0) {
      const employeeId =
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : "";
      const exists = (employees || []).find((e) => String(e.id) === employeeId);
      if (exists) {
        setSelectedEmployee(employeeId);
      } else {
        setSelectedEmployee("");
      }
    }
  }, [editData, employees]);

  useEffect(() => {
    if (editData && currencies && currencies.length > 0) {
      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      const exists = (currencies || []).find(
        (c) => String(c.id) === currencyId
      );
      if (exists) {
        setSelectedCurrency(currencyId);
      } else {
        setSelectedCurrency("");
      }
    }
  }, [editData, currencies]);

  useEffect(() => {
    if (!selectedCurrency || !selectedShopId) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedShopId, accounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    ["advance-edit-rate-direct", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["advance-edit-rate-reverse", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );
  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);
  useEffect(() => {
    if (!editData || !exchangeRatePreview) return;
    const currentRateSelection = `${selectedCurrency}:${selectedAccountId}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [editData, exchangeRatePreview, selectedCurrency, selectedAccountId]);
  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShopId);
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    formData.append("year", year);
    formData.append("month", month);
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedEmployee("");
      setSelectedCurrency("");
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setYear("");
      setMonth("");
      setPaymentDate("");
      setNotes("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("AdvanceIndex.titleEdit")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.employee")}
            fullWidth
            required
            value={selectedEmployee || ""}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (employees || []).map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.currency")}
            fullWidth
            required
            value={selectedCurrency || ""}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("AdvanceIndex.amount")}
            type="number"
            fullWidth
            required
            value={amount || ""}
            onChange={(e) => setAmount(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedCurrency || !selectedShopId || accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : accounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("AdvanceIndex.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("AdvanceIndex.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />
          <TextField
            margin="dense"
            label={t("AdvanceIndex.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
          />
          {!exchangeRatePreview && selectedCurrency && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("AdvanceIndex.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.year")}
            fullWidth
            required
            value={year || ""}
            onChange={(e) => setYear(e.target.value)}
          >
            {Array.from({ length: 26 }, (_, i) => 2015 + i).map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.month")}
            fullWidth
            required
            value={month || ""}
            onChange={(e) => setMonth(e.target.value)}
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label={t("AdvanceIndex.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate || ""}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("AdvanceIndex.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes || ""}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("AdvanceIndex.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !selectedAccountId ||
            !exchangeRate ||
            !year ||
            !month ||
            !paymentDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("AdvanceIndex.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvanceEdit;
